import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';
import { adminAPI, creditAPI } from '../../services/api';
import Layout from '../../components/Layout';

const AddCredits = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only fetch users when input has at least 3 characters
    if (inputValue.length >= 3) {
      fetchUsers(inputValue);
    } else {
      setUsers([]);
    }
  }, [inputValue]);

  const fetchUsers = async (searchTerm) => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers('Client', 1000, 0);
      if (response.success) {
        // Filter users by name, email, or company name
        const filtered = response.data.filter(user => {
          const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
          const email = user.email.toLowerCase();
          const company = (user.companyName || '').toLowerCase();
          const search = searchTerm.toLowerCase();
          
          return fullName.includes(search) || 
                 email.includes(search) || 
                 company.includes(search);
        });
        setUsers(filtered);
      }
    } catch (error) {
      // console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      const response = await creditAPI.addCredits(
        selectedUser.userId,
        parseFloat(amount)
      );

      if (response.success) {
        toast.success(`Successfully added ₹${amount} credits to ${selectedUser.firstName} ${selectedUser.lastName}`);
        
        // Update the selected user's credit info
        setSelectedUser({
          ...selectedUser,
          credits: response.data
        });
        
        // Refresh users list with current search term
        if (inputValue.length >= 3) {
          fetchUsers(inputValue);
        }
        
        // Clear amount
        setAmount('');
      }
    } catch (error) {
      // console.error('Error adding credits:', error);
      toast.error(error.response?.data?.message || 'Failed to add credits');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && users.length === 0 && inputValue.length < 3) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Add Credits to User
          </Typography>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Start typing to search for users...
            </Typography>
          </Paper>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Add Credits to User
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Add Credits Form */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Credit Addition Form
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) =>
                    `${option.firstName} ${option.lastName} (${option.email})`
                  }
                  value={selectedUser}
                  onChange={(event, newValue) => setSelectedUser(newValue)}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  open={open && inputValue.length >= 3}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  loading={loading}
                  noOptionsText={
                    inputValue.length < 3
                      ? 'Type at least 3 characters to search'
                      : 'No users found'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      placeholder="Search by name, email, or company"
                      required
                      helperText="Type at least 3 characters to search"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.firstName} {option.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.email} • {option.companyName}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />

                <TextField
                  fullWidth
                  label="Amount (₹)"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Enter the number of credits to add"
                  required
                />

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAddCredits}
                  disabled={submitting || !selectedUser || !amount}
                  startIcon={submitting ? <CircularProgress size={20} /> : <AccountBalanceWalletIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {submitting ? 'Adding Credits...' : 'Add Credits'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* User Details */}
          <Grid item xs={12} md={6}>
            {selectedUser ? (
              <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  User Details
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: '#1976d2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Company
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedUser.companyName}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Phone Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedUser.phoneNumber}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedUser.category}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Current Credit Balance
                </Typography>

                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={4}>
                    <Card sx={{ backgroundColor: '#e3f2fd' }}>
                      <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Total
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          ₹{selectedUser.credits.total.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ backgroundColor: '#fff3e0' }}>
                      <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Used
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#f57c00', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          ₹{selectedUser.credits.used.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ backgroundColor: '#e8f5e9' }}>
                      <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Remaining
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#388e3c', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          ₹{selectedUser.credits.remaining.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            ) : (
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5'
                }}
              >
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Select a user to view their details and add credits
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default AddCredits;
